import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { SearchBar } from './components/SearchBar';
import { Sidebar } from './components/Sidebar';
import { StockGrid } from './components/StockGrid';
import { StockDetailPage } from './pages/StockDetailPage';
import { LandingPage } from './pages/LandingPage';
import { stockApi, tagApi } from './lib/api';
import { useFilterStore } from './store/useFilterStore';
import { useMemo } from 'react';
import type { Tag } from './types/api';
import { TrendingUp, ArrowUpDown, Menu } from 'lucide-react';
import { useState } from 'react';
import { FeedbackButton } from './components/FeedbackButton';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function Dashboard() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const {
    searchQuery,
    selectedSectors,
    selectedIndustries,
    selectedTags,
    scoreFilters,
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder,
  } = useFilterStore();

  // Fetch stocks data
  const { data: stocks = [], isLoading } = useQuery({
    queryKey: ['stocks'],
    queryFn: stockApi.getFourScores,
  });

  // Fetch all assets with tags
  const { data: assetsWithTags = [] } = useQuery({
    queryKey: ['assetsWithTags'],
    queryFn: tagApi.getAllAssetsWithTags,
  });

  // Extract unique tags from assetsWithTags for the filter dropdown
  const tags = useMemo(() => {
    const tagMap = new Map();
    assetsWithTags.forEach((asset: any) => {
      if (asset.tags && Array.isArray(asset.tags)) {
        asset.tags.forEach((tag: any) => {
          if (!tagMap.has(tag.id)) {
            tagMap.set(tag.id, tag);
          }
        });
      }
    });
    return Array.from(tagMap.values()).sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [assetsWithTags]);

  // Merge stocks with their tags
  const stocksWithTags = useMemo(() => {
    return stocks.map((stock) => {
      const assetWithTags = assetsWithTags.find((a: any) => a.symbol === stock.symbol);
      return {
        ...stock,
        tags: assetWithTags?.tags || [],
      };
    });
  }, [stocks, assetsWithTags]);

  // Filter and sort stocks
  const filteredStocks = useMemo(() => {
    let result = [...stocksWithTags];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (stock) =>
          (stock.symbol && stock.symbol.toLowerCase().includes(query)) ||
          (stock.name && stock.name.toLowerCase().includes(query))
      );
    }

    // Sector filter
    if (selectedSectors.length > 0) {
      result = result.filter((stock) =>
        stock.sector ? selectedSectors.includes(stock.sector) : false
      );
    }

    // Industry filter
    if (selectedIndustries.length > 0) {
      result = result.filter((stock) =>
        stock.industry ? selectedIndustries.includes(stock.industry) : false
      );
    }

    // Score tier filters
    const hasScoreFilters = Object.values(scoreFilters).some((f) => f.tiers.length > 0);
    if (hasScoreFilters) {
      result = result.filter((stock) => {
        const scoreChecks = [
          scoreFilters.value.tiers.length === 0 || scoreFilters.value.tiers.includes(stock.value_tier),
          scoreFilters.growth.tiers.length === 0 || scoreFilters.growth.tiers.includes(stock.growth_tier),
          scoreFilters.health.tiers.length === 0 || scoreFilters.health.tiers.includes(stock.health_tier),
          scoreFilters.momentum.tiers.length === 0 || scoreFilters.momentum.tiers.includes(stock.momentum_tier),
        ];
        return scoreChecks.every((check) => check);
      });
    }

    // Tag filter
    if (selectedTags.length > 0) {
      result = result.filter((stock) =>
        stock.tags?.some((tag: Tag) => selectedTags.includes(tag.id))
      );
    }

    // Sort
    result.sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      if (sortBy === 'name') {
        aValue = a.name;
        bValue = b.name;
      } else {
        aValue = a[sortBy];
        bValue = b[sortBy];
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortOrder === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
    });

    return result;
  }, [stocksWithTags, searchQuery, selectedSectors, selectedIndustries, selectedTags, scoreFilters, sortBy, sortOrder]);

  // Extract unique sectors from stocks
  const sectors = useMemo(() => {
    const uniqueSectors = new Set<string>();
    stocksWithTags.forEach((stock) => {
      if (stock.sector) {
        uniqueSectors.add(stock.sector);
      }
    });
    return Array.from(uniqueSectors).sort();
  }, [stocksWithTags]);

  // Extract unique industries from stocks
  // If sectors are selected, only show industries from those sectors
  const industries = useMemo(() => {
    const uniqueIndustries = new Set<string>();
    stocksWithTags.forEach((stock) => {
      if (stock.industry) {
        // If sectors are selected, only include industries from selected sectors
        if (selectedSectors.length > 0) {
          if (stock.sector && selectedSectors.includes(stock.sector)) {
            uniqueIndustries.add(stock.industry);
          }
        } else {
          // If no sectors selected, show all industries
          uniqueIndustries.add(stock.industry);
        }
      }
    });
    return Array.from(uniqueIndustries).sort();
  }, [stocksWithTags, selectedSectors]);

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        background: 'radial-gradient(ellipse at top, #1a1a1a 0%, #0F0F0F 50%, #0a0a0a 100%)',
        position: 'relative'
      }}
    >
      {/* Subtle noise texture overlay */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }}
      />
      
      {/* Header */}
      <header className="border-b backdrop-blur-sm sticky top-0 z-40" style={{ borderColor: 'rgba(255, 255, 255, 0.1)', backgroundColor: 'rgba(15, 15, 15, 0.8)' }}>
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              {/* Hamburger Menu - Mobile Only */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
                style={{ color: '#9E9E9E' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.color = '#F9F9F9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#9E9E9E';
                }}
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="p-1.5 sm:p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: 'rgba(62, 207, 142, 0.1)' }}>
                <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: '#3ECF8E' }} />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold truncate" style={{ color: '#F9F9F9' }}>Stock Analysis Dashboard</h1>
                <p className="text-xs sm:text-sm mt-0.5 sm:mt-1 hidden sm:block" style={{ color: '#9E9E9E' }}>
                  Real-time stock scoring and analysis
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <div className="flex-1 w-full">
              <SearchBar />
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs sm:text-sm whitespace-nowrap" style={{ color: '#9E9E9E' }}>Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-2 sm:px-3 py-2 border rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 transition-colors min-h-[44px]"
                style={{
                  backgroundColor: '#1E1E1E',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  color: '#F9F9F9'
                }}
              >
                <option value="value_score">Best Value</option>
                <option value="growth_score">Best Growth</option>
                <option value="health_score">Best Health</option>
                <option value="momentum_score">Best Momentum</option>
                <option value="name">Name</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 border rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                style={{
                  backgroundColor: '#1E1E1E',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  color: '#F9F9F9'
                }}
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2A2A2A';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#1E1E1E';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
                }}
              >
                <ArrowUpDown className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-3 sm:mt-4 flex items-center gap-4 sm:gap-6 text-xs sm:text-sm" style={{ color: '#9E9E9E' }}>
            <span>
              Showing: <span className="font-semibold" style={{ color: '#F9F9F9' }}>{filteredStocks.length}</span> of{' '}
              <span className="font-semibold" style={{ color: '#F9F9F9' }}>{stocksWithTags.length}</span>
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex relative">
        <Sidebar 
          sectors={sectors} 
          industries={industries} 
          tags={tags}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <main className="flex-1 p-4 sm:p-6 md:p-8 w-full">
          <StockGrid 
            stocks={filteredStocks} 
            isLoading={isLoading}
            onStockClick={(stock) => navigate(`/stock/${stock.symbol}`)}
          />
        </main>
      </div>

      {/* Floating Feedback Button */}
      <FeedbackButton />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/stock/:symbol" element={<StockDetailPage />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;

